import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  CreateRecordAuditSeriesDefinitionApiArg,
  CreateRecordAuditSeriesDefinitionApiResponse,
  GetRecordAuditSeriesDefinitionApiArg,
  GetRecordAuditSeriesDefinitionApiResponse,
  GetRecordAuditSeriesDefinitionListApiArg,
  getRecordAuditSeriesDefinitionListApiResponseSchema,
  GetRecordAuditSeriesDefinitionListApiResponse,
  recordAuditSeriesDefinitionResponseBodySchema,
  UpdateRecordAuditSeriesDefinitionApiArg,
  UpdateRecordAuditSeriesDefinitionApiResponse,
} from './types';

// Series definitions share the `RecordAudit` tag type; the `SERIES_DEFINITION:` id prefix keeps
// them apart from audit ids.
const SERIES_DEFINITION_LIST_TAG = {type: 'RecordAudit', id: 'SERIES_DEFINITION_LIST'} as const;
const seriesDefinitionTag = (seriesDefinitionId: string) =>
  ({type: 'RecordAudit', id: `SERIES_DEFINITION:${seriesDefinitionId}`}) as const;

const injectedRtkApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getRecordAuditSeriesDefinitionList: build.query<
      GetRecordAuditSeriesDefinitionListApiResponse,
      GetRecordAuditSeriesDefinitionListApiArg
    >({
      query: () => ({
        url: `/dms/v1/record-audit/series-definition`,
      }),
      providesTags: [SERIES_DEFINITION_LIST_TAG],
      extraOptions: {
        responseSchema: getRecordAuditSeriesDefinitionListApiResponseSchema,
      },
    }),
    getRecordAuditSeriesDefinition: build.query<
      GetRecordAuditSeriesDefinitionApiResponse,
      GetRecordAuditSeriesDefinitionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/series-definition/${queryArg.seriesDefinitionId}`,
      }),
      providesTags: (result, error, queryArg) => [seriesDefinitionTag(queryArg.seriesDefinitionId)],
      extraOptions: {
        responseSchema: recordAuditSeriesDefinitionResponseBodySchema,
      },
    }),
    createRecordAuditSeriesDefinition: build.mutation<
      CreateRecordAuditSeriesDefinitionApiResponse,
      CreateRecordAuditSeriesDefinitionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/series-definition`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess([SERIES_DEFINITION_LIST_TAG]),
    }),
    updateRecordAuditSeriesDefinition: build.mutation<
      UpdateRecordAuditSeriesDefinitionApiResponse,
      UpdateRecordAuditSeriesDefinitionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/series-definition/${queryArg.seriesDefinitionId}`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: UpdateRecordAuditSeriesDefinitionApiArg) => [
          SERIES_DEFINITION_LIST_TAG,
          seriesDefinitionTag(queryArg.seriesDefinitionId),
        ]
      ),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetRecordAuditSeriesDefinitionListQuery,
  useGetRecordAuditSeriesDefinitionQuery,
  useCreateRecordAuditSeriesDefinitionMutation,
  useUpdateRecordAuditSeriesDefinitionMutation,
} = injectedRtkApi;
